import { z } from 'zod';
import { apiGetParsed, apiPostParsed, apiDelete } from './client';

/**
 * S20-C0 — 案分配 + 企业↔企服分配 API。
 *
 * 后端契约(BE sub-agent 同步实现):
 *   GET    /enterprises/{id}/staff-assignments
 *           → { items: AssignmentRow[], primary_account_id: UUID|null }
 *   POST   /enterprises/{id}/staff-assignments
 *           body: { account_id, role: 'primary'|'secondary' }
 *   DELETE /enterprises/{id}/staff-assignments/{account_id}
 *
 *   GET    /declaration-cases/{id}/assignments
 *           → { items: AssignmentRow[], primary_account_id: UUID|null }
 *   POST   /declaration-cases/{id}/assignments
 *           body: { account_id, notes? }    // 只能加 assistant
 *   DELETE /declaration-cases/{id}/assignments/{account_id}
 *   POST   /declaration-cases/{id}/reassign
 *           body: { owner_account_id, note? }
 *
 * 走全 zod parse(apiGetParsed/apiPostParsed)— 防契约漂移。
 */

// ============================================================================
// 公共 schema
// ============================================================================

/** 单条分配记录(企业↔账号 / 案↔账号 通用结构) */
export const assignmentRowSchema = z.object({
  id: z.string(),
  account_id: z.string(),
  account_name: z.string().nullable().optional(),
  account_email: z.string().nullable().optional(),
  /** 企业↔账号:'primary'|'secondary';案↔账号:'owner'|'assistant' */
  role: z.string(),
  assigned_at: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
});
export type AssignmentRow = z.infer<typeof assignmentRowSchema>;

export const assignmentListSchema = z.object({
  items: z.array(assignmentRowSchema),
  /** 企业↔企服:primary account id;案↔账号:owner account id */
  primary_account_id: z.string().nullable(),
});
export type AssignmentList = z.infer<typeof assignmentListSchema>;

function _safe(seg: string): string {
  return encodeURIComponent(seg);
}

// ============================================================================
// 企业 ↔ 企服 分配
// ============================================================================

export interface AddEnterpriseStaffBody {
  account_id: string;
  role: 'primary' | 'secondary';
}

export async function listEnterpriseStaff(
  enterpriseId: string,
): Promise<AssignmentList> {
  return apiGetParsed(
    `/enterprises/${_safe(enterpriseId)}/staff-assignments`,
    assignmentListSchema,
  );
}

export async function addEnterpriseStaff(
  enterpriseId: string,
  body: AddEnterpriseStaffBody,
): Promise<AssignmentRow> {
  return apiPostParsed(
    `/enterprises/${_safe(enterpriseId)}/staff-assignments`,
    assignmentRowSchema,
    body,
  );
}

export async function removeEnterpriseStaff(
  enterpriseId: string,
  accountId: string,
): Promise<{ id?: string }> {
  return apiDelete(
    `/enterprises/${_safe(enterpriseId)}/staff-assignments/${_safe(accountId)}`,
  );
}

// ============================================================================
// 申报案 ↔ 账号 分配
// ============================================================================

export interface AddCaseAssistantBody {
  account_id: string;
  notes?: string | null;
}

export interface ReassignCaseBody {
  owner_account_id: string;
  note?: string | null;
}

export async function listCaseAssignments(
  caseId: string,
): Promise<AssignmentList> {
  return apiGetParsed(
    `/declaration-cases/${_safe(caseId)}/assignments`,
    assignmentListSchema,
  );
}

export async function addCaseAssistant(
  caseId: string,
  body: AddCaseAssistantBody,
): Promise<AssignmentRow> {
  return apiPostParsed(
    `/declaration-cases/${_safe(caseId)}/assignments`,
    assignmentRowSchema,
    body,
  );
}

export async function removeCaseAssistant(
  caseId: string,
  accountId: string,
): Promise<{ id?: string }> {
  return apiDelete(
    `/declaration-cases/${_safe(caseId)}/assignments/${_safe(accountId)}`,
  );
}

export async function reassignCase(
  caseId: string,
  body: ReassignCaseBody,
): Promise<AssignmentRow> {
  return apiPostParsed(
    `/declaration-cases/${_safe(caseId)}/reassign`,
    assignmentRowSchema,
    body,
  );
}
